/**
 * PAGE VISIBILITY BANNER
 * 
 * A reusable component that displays the public/private status of a page
 * and provides quick actions for sharing or adjusting privacy settings.
 * 
 * Usage:
 * <PageVisibilityBanner 
 *   isPublic={true}
 *   publicPath="/creations/username"
 *   pageType="creations"
 *   onToggleVisibility={() => handleToggle()} // Optional
 * />
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Globe, 
  Lock, 
  Copy, 
  Check, 
  Share2, 
  Settings, 
  ExternalLink,
  Eye,
  EyeOff
} from 'lucide-react';

interface PageVisibilityBannerProps {
  /** Whether this page is currently public */
  isPublic: boolean;
  /** The public URL path (e.g., "/creations/username") */
  publicPath: string;
  /** Type of page for display text */
  pageType: 'collection' | 'creations' | 'wishlist' | 'destash' | 'profile';
  /** Optional callback when visibility is toggled (if inline toggle is desired) */
  onToggleVisibility?: () => void;
  /** Optional callback for preview button (overrides default link behavior) */
  onPreview?: () => void;
  /** Whether currently in preview mode */
  isPreviewMode?: boolean;
  /** Whether toggle is currently loading */
  isToggling?: boolean;
  /** Custom class name */
  className?: string;
}

const pageTypeLabels: Record<string, string> = {
  collection: 'collection',
  creations: 'creations page',
  wishlist: 'wishlist',
  destash: 'destash list',
  profile: 'profile',
};

const PageVisibilityBanner: React.FC<PageVisibilityBannerProps> = ({
  isPublic,
  publicPath,
  pageType,
  onToggleVisibility,
  onPreview,
  isPreviewMode = false,
  isToggling = false,
  className = '',
}) => {
  const [copied, setCopied] = useState(false);

  const fullUrl = `${window.location.origin}${publicPath}`;
  const label = pageTypeLabels[pageType] || 'page';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Check out my ${label}!`,
          url: fullUrl,
        });
      } catch (err) {
        // User cancelled or share failed - fall back to copy
        if ((err as Error).name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      // Fallback for browsers without Web Share API
      handleCopyLink();
    }
  };

  if (isPublic) {
    // PUBLIC STATE - or PREVIEW MODE
    return (
      <div className={`backdrop-blur-md border rounded-xl px-4 py-3 shadow-lg ${
        isPreviewMode 
          ? 'bg-amber-500/90 border-amber-400' 
          : 'bg-slate-800/90 border-green-500/30'
      } ${className}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Status */}
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isPreviewMode ? 'bg-amber-600/50' : 'bg-green-500/20'
            }`}>
              {isPreviewMode ? (
                <Eye className="icon-sm text-white" />
              ) : (
                <Globe className="icon-sm text-green-400" />
              )}
            </div>
            <div>
              <p className={`text-sm font-semibold font-body ${isPreviewMode ? 'text-white' : 'text-glass'}`}>
                {isPreviewMode ? `Previewing as visitor` : `This ${label} is public`}
              </p>
              <p className={`text-xs font-body ${isPreviewMode ? 'text-amber-100' : 'text-glass-secondary'}`}>
                {isPreviewMode ? `This is what others see` : `Anyone with the link can view it`}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Copy Link Button */}
            <button
              onClick={handleCopyLink}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-all font-body ${
                copied
                  ? 'bg-green-500 text-white'
                  : isPreviewMode
                    ? 'bg-amber-600/80 text-white hover:bg-amber-600 border border-amber-400/50'
                    : 'bg-slate-700/80 text-glass hover:bg-slate-600/80 border border-slate-600'
              }`}
            >
              {copied ? (
                <>
                  <Check className="icon-sm" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="icon-sm" />
                  Copy Link
                </>
              )}
            </button>

            {/* Share Button (uses Web Share API on mobile) */}
            <button
              onClick={handleShare}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-default font-body ${
                isPreviewMode
                  ? 'bg-amber-600 hover:bg-amber-700 text-white'
                  : 'bg-green-600/80 hover:bg-green-600 text-white'
              }`}
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </button>

            {/* Preview Button/Link */}
            {onPreview ? (
              <button
                onClick={onPreview}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm transition-default font-body ${
                  isPreviewMode
                    ? 'bg-white text-amber-700 hover:bg-amber-50 font-semibold'
                    : 'text-glass-secondary hover:text-glass hover:bg-slate-700/60'
                }`}
                title={isPreviewMode ? 'Exit preview mode' : 'Preview as visitor'}
              >
                {isPreviewMode ? <EyeOff className="icon-sm" /> : <ExternalLink className="icon-sm" />}
                <span className="hidden sm:inline">{isPreviewMode ? 'Exit Preview' : 'Preview'}</span>
              </button>
            ) : (
              <Link
                to={publicPath}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm transition-default font-body ${
                  isPreviewMode
                    ? 'text-amber-100 hover:text-white hover:bg-amber-600/60'
                    : 'text-glass-secondary hover:text-glass hover:bg-slate-700/60'
                }`}
                title="Preview public page"
              >
                <ExternalLink className="icon-sm" />
                <span className="hidden sm:inline">Preview</span>
              </Link>
            )}

            {/* Toggle or Settings */}
            {onToggleVisibility ? (
              <button
                onClick={onToggleVisibility}
                disabled={isToggling}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-default font-body ${
                  isPreviewMode
                    ? 'text-amber-100 hover:text-white hover:bg-amber-600/60'
                    : 'text-glass-secondary hover:text-glass hover:bg-slate-700/60'
                }`}
                title="Make private"
              >
                <EyeOff className="icon-sm" />
                <span className="hidden sm:inline">Make Private</span>
              </button>
            ) : (
              <Link
                to="/settings"
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm transition-default font-body ${
                  isPreviewMode
                    ? 'text-amber-100 hover:text-white hover:bg-amber-600/60'
                    : 'text-glass-secondary hover:text-glass hover:bg-slate-700/60'
                }`}
                title="Privacy settings"
              >
                <Settings className="icon-sm" />
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  // PRIVATE STATE
  return (
    <div className={`backdrop-blur-md bg-slate-800/90 border border-purple-500/30 rounded-xl px-4 py-3 shadow-lg ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Status */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
            <Lock className="icon-sm text-purple-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-glass font-body">
              This {label} is private
            </p>
            <p className="text-xs text-glass-secondary font-body">
              Only you can see it
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {onToggleVisibility ? (
            <button
              onClick={onToggleVisibility}
              disabled={isToggling}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600/80 hover:bg-purple-600 text-white rounded-lg font-medium text-sm transition-default disabled:opacity-50 font-body"
            >
              {isToggling ? (
                <>
                  <div className="w-4 h-4 border-2 border-slate-600 border-t-white rounded-full animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Eye className="icon-sm" />
                  Make Public
                </>
              )}
            </button>
          ) : (
            <Link
              to="/settings"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600/80 hover:bg-purple-600 text-white rounded-lg font-medium text-sm transition-default font-body"
            >
              <Settings className="icon-sm" />
              Change in Settings
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageVisibilityBanner;
